import katex from 'katex';
import 'katex/contrib/mhchem';

export type MathSegment = { type: 'text' | 'inline' | 'display'; content: string };

function restoreBrokenJsonEscapes(text: string): string {
  return text
    // \bpi -> \pi, \bcap -> \cap, \bperp -> \perp
    .replace(/\u0008(?=[A-Za-z])/g, '\\')
    // \frac, \forall -> form-feed escape ate the "f"
    .replace(/\u000C(?=[A-Za-z])/g, '\\f')
    // \tan, \theta, \times -> tab escape ate the "t"
    .replace(/\u0009(?=[A-Za-z])/g, '\\t')
    // \neq, \nu -> newline escape ate the "n"
    .replace(/\u000A(?=[A-Za-z])/g, '\\n')
    // \rho, \right -> carriage return escape ate the "r"
    .replace(/\u000D(?=[A-Za-z])/g, '\\r');
}

/** Đồng bộ với edu-app-backend/scripts/katex-mixed-html.mjs */
const LATEX_CMD_ALIASES =
  'ln|log|frac|dfrac|tfrac|sqrt|root|int|sum|prod|coprod|lim|limsup|liminf|infty|partial|cdot|times|div|pm|mp|cap|cup|setminus|subseteq|supseteq|subset|supset|in|notin|ni|forall|exists|nabla|ell|emptyset|varnothing|bigcup|bigcap|oint|iint|iiint|idotsint|ldots|cdots|vdots|ddots|mathbb|mathrm|mathbf|mathit|mathcal|mathfrak|text|textbf|textit|operatorname|sin|cos|tan|cot|sec|csc|arcsin|arccos|arctan|sinh|cosh|tanh|coth|det|dim|ker|hom|deg|gcd|lcm|max|min|sup|inf|exp|Pr|arg|angle|triangle|perp|parallel|leq|geq|neq|approx|sim|equiv|propto|simeq|cong|models|mid|mapsto|to|rightarrow|leftarrow|leftrightarrow|Rightarrow|Leftarrow|Leftrightarrow|implies|iff|boxed|Box|square|diamond|star|bullet|circ|ast|wedge|vee|neg|oplus|otimes|bigoplus|bigotimes|begin|end|left|right|middle|bigl|bigr|Bigl|Bigr|biggl|biggr|overline|underline|widehat|widetilde|vec|bar|hat|dot|ddot|dddot|tilde|binom|choose|genfrac';

export function normalizeMathInput(text: string): string {
  let s = restoreBrokenJsonEscapes(text ?? '').replace(/[\u0000-\u0007\u000B-\u001F\u007F]/g, ' ');

  const doubleCmd = new RegExp(`\\\\\\\\(\\s*)(${LATEX_CMD_ALIASES})\\b`, 'g');
  s = s.replace(doubleCmd, (_m, _sp, cmd) => `\\${cmd}`);

  s = s
    .replace(/\^\{\\\{([^}]*?)\\\}\}/g, '^{$1}')
    .replace(/_\{\\\{([^}]*?)\\\}\}/g, '_{$1}')
    .replace(/\^\{\\\\\{([^}]*?)\\\\\}\}/g, '^{$1}')
    .replace(/_\{\\\\\{([^}]*?)\\\\\}\}/g, '_{$1}');

  s = s.replace(/\\Box\b/g, '\\square');

  return s
    .replace(/\\mathbb\s*([RNCZQ])\b/g, '\\mathbb{$1}')
    .replace(/\\(vec|bar|hat|dot|tilde|underline|overline)\s*([A-Za-z0-9])\b/g, '\\$1{$2}')
    .replace(/🢢/g, '⇒')
    .replace(
      /(?<![\\])\b(arcsin|arccos|arctan|sin|cos|tan|cot|log|ln)\s+([A-Za-z0-9])/g,
      '\\$1 $2',
    )
    .replace(/([A-Za-z0-9])\s+\\mathbb\{/g, '$1 \\in \\mathbb{')
    .replace(/\\in\s+\\in\b/g, '\\in ')
    .replace(/([A-Za-z0-9])\s+\\in\s+\\mathbb\{/g, '$1 \\in \\mathbb{')
    .replace(/([A-Za-z0-9])\s+\\\s+mathbb\{/g, '$1 \\in \\mathbb{')
    .replace(/\\setminus\s+\\\{/g, '\\setminus \\{')
    .replace(/\s+\\([)\]}])/g, ' \\$1')
    .replace(/\s+\$/g, '$')
    .replace(/\$\s*\\\s*$/g, '$')
    .replace(/\u0008+/g, ' ')
    // Gộp 2+ ký tự whitespace KHÔNG phải xuống dòng thành 1 dấu cách (giữ nguyên \n
    // để các trường nhiều dòng như mục tiêu giáo án hiển thị đúng phân mục 2.1/2.2/2.3...)
    .replace(/[^\S\n]{2,}/g, ' ');
}

function indexOfUnescapedDollar(str: string, start: number): number {
  for (let k = start; k < str.length; k += 1) {
    if (str[k] !== '$') continue;
    if (k > 0 && str[k - 1] === '\\') continue;
    return k;
  }
  return -1;
}

/**
 * Tách chuỗi thành đoạn văn bản và LaTeX.
 * Hỗ trợ: $$...$$, \\[...\\], \\(...\\), $...$ (inline, không trùng $$).
 * Nội dung không có delimiter được giữ nguyên (tương thích ngược).
 */
export function splitMixedMath(text: string): MathSegment[] {
  text = normalizeMathInput(text);
  const segments: MathSegment[] = [];
  let i = 0;
  const n = text.length;
  let buf = '';
  const flush = () => {
    if (buf.length) {
      segments.push({ type: 'text', content: buf });
      buf = '';
    }
  };

  while (i < n) {
    if (text.startsWith('$$', i)) {
      flush();
      i += 2;
      const start = i;
      const end = text.indexOf('$$', start);
      if (end === -1) {
        buf += '$$' + text.slice(start);
        break;
      }
      segments.push({ type: 'display', content: text.slice(start, end) });
      i = end + 2;
      continue;
    }
    if (text.startsWith('\\[', i)) {
      flush();
      i += 2;
      const start = i;
      const end = text.indexOf('\\]', start);
      if (end === -1) {
        buf += '\\[' + text.slice(start);
        break;
      }
      segments.push({ type: 'display', content: text.slice(start, end) });
      i = end + 2;
      continue;
    }
    if (text.startsWith('\\(', i)) {
      flush();
      i += 2;
      const start = i;
      const end = text.indexOf('\\)', start);
      if (end === -1) {
        buf += '\\(' + text.slice(start);
        break;
      }
      segments.push({ type: 'inline', content: text.slice(start, end) });
      i = end + 2;
      continue;
    }
    if (text[i] === '$' && !(i > 0 && text[i - 1] === '\\')) {
      flush();
      i += 1;
      const start = i;
      const end = indexOfUnescapedDollar(text, start);
      if (end === -1) {
        buf += '$' + text.slice(start);
        break;
      }
      segments.push({ type: 'inline', content: text.slice(start, end) });
      i = end + 1;
      continue;
    }
    buf += text[i];
    i += 1;
  }
  flush();
  return segments;
}

function legacyMathToLatex(expr: string): string {
  return normalizeMathInput(expr)
    .trim()
    .replace(/÷/g, '/')
    .replace(/×/g, '\\times ')
    .replace(/√\s*\(([^()]+)\)/g, '\\sqrt{$1}')
    .replace(/√([A-Za-z0-9]+)/g, '\\sqrt{$1}')
    .replace(/\b(sin|cos|tan|cot|log|ln)\s*\(/g, '\\$1(')
    .replace(/(\d+)\s*\/\s*(\d+)(?![\d/])/g, '\\frac{$1}{$2}');
}

function isMathTokenChar(char: string): boolean {
  return /[A-Za-z0-9)\]}]/.test(char);
}

function isRightMathStart(char: string): boolean {
  return /[A-Za-z0-9([{\-\\]/.test(char);
}

function readBalanced(text: string, start: number, open: string, close: string): number {
  let depth = 0;
  let i = start;
  while (i < text.length) {
    if (text[i] === open) depth += 1;
    if (text[i] === close) {
      depth -= 1;
      if (depth === 0) return i + 1;
    }
    i += 1;
  }
  return text.length;
}

function readRightMathToken(text: string, start: number): number {
  if (start >= text.length) return start;
  const ch = text[start];
  if (ch === '\\' && text[start + 1] === '{') {
    const end = text.indexOf('\\}', start + 2);
    return end === -1 ? text.length : end + 2;
  }
  if (ch === '(') return readBalanced(text, start, '(', ')');
  if (ch === '[') return readBalanced(text, start, '[', ']');
  if (ch === '{') return readBalanced(text, start, '{', '}');

  if (ch === '\\') {
    let i = start + 1;
    while (i < text.length && /[A-Za-z]/.test(text[i])) i += 1;
    while (i < text.length && /\s/.test(text[i])) i += 1;
    if (i < text.length && text[i] === '{') {
      i = readBalanced(text, i, '{', '}');
    }
    return i;
  }

  let i = start;
  while (i < text.length && /[A-Za-z0-9_^]/.test(text[i])) i += 1;
  return i;
}

function findLatexCommandRanges(text: string): Array<{ start: number; end: number }> {
  const commandRe =
    /\\(?:sqrt|frac|dfrac|perp|parallel|angle|triangle|cdot|times|div|leq|geq|neq|approx|sim|pi|alpha|beta|gamma|theta|lambda|mu|Delta|sum|int|lim|to|rightarrow|leftarrow|leftrightarrow|mathrm|mathbb|text|overline|vec|bar|hat|dot|tilde|underline|ce|pu|sin|cos|tan|cot|log|ln|in|setminus|circ|arctan|arcsin|arccos|infty|partial|nabla|sigma|delta|rho|phi|psi|nu|xi|eta|zeta|omega|Omega|varepsilon|vartheta|varphi|mathbf|mathcal|mathit|mathfrak|binom|equiv|propto|ldots|cdots|vdots|ddots|iff|implies|hbar|prime)\b/g;

  const ranges: Array<{ start: number; end: number }> = [];

  for (const match of text.matchAll(commandRe)) {
    const idx = match.index ?? 0;
    let start = idx;
    let end = idx + match[0].length;

    let left = start;
    while (left > 0 && /\s/.test(text[left - 1])) left -= 1;
    while (left > 0 && isMathTokenChar(text[left - 1])) left -= 1;
    if (left < start) start = left;

    let cursor = end;
    while (cursor < text.length) {
      while (cursor < text.length && /\s/.test(text[cursor])) cursor += 1;
      if (cursor < text.length && text[cursor] === '{') {
        cursor = readBalanced(text, cursor, '{', '}');
        end = cursor;
        continue;
      }
      break;
    }

    let right = end;
    while (right < text.length && /\s/.test(text[right])) right += 1;
    if (right < text.length && isRightMathStart(text[right])) {
      right = readRightMathToken(text, right);
      end = right;
    }

    ranges.push({ start, end });
  }

  return ranges;
}

export function splitLegacyMathText(text: string): MathSegment[] {
  text = normalizeMathInput(text);
  const plainPattern =
    /((?:[-+]?\d+\s*)?(?:arcsin|arccos|arctan|sin|cos|tan|cot|log|ln)\([^)\n]+\)|[A-Za-z][A-Za-z0-9']*\([^)\n]+\)|[A-Za-z]\s*=\s*[A-Za-z0-9+\-*/^() ]+|(?:\d+\s*\/\s*\d+\s*)?[A-Za-z0-9]+(?:\^[A-Za-z0-9()+-]+)+)/g;

  const ranges: Array<{ start: number; end: number }> = [];

  for (const match of text.matchAll(plainPattern)) {
    const value = match[0];
    const start = match.index ?? 0;
    const end = start + value.length;
    ranges.push({ start, end });
  }

  ranges.push(...findLatexCommandRanges(text));

  if (!ranges.length) {
    return [{ type: 'text', content: text }];
  }

  ranges.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: Array<{ start: number; end: number }> = [];

  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (!last || range.start > last.end) {
      merged.push({ ...range });
      continue;
    }
    last.end = Math.max(last.end, range.end);
  }

  const segments: MathSegment[] = [];
  let lastIndex = 0;

  for (const range of merged) {
    if (range.start > lastIndex) {
      segments.push({ type: 'text', content: text.slice(lastIndex, range.start) });
    }

    const rawExpr = text.slice(range.start, range.end);
    segments.push({ type: 'inline', content: legacyMathToLatex(rawExpr) });
    lastIndex = range.end;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return segments;
}

/** Gộp nhiều đoạn inline (hoặc display) liền nhau → một ảnh / một lần render, giảm số fragment và request. */
export function mergeAdjacentMathSegments(segments: MathSegment[]): MathSegment[] {
  if (segments.length <= 1) return segments;
  const out: MathSegment[] = [];
  let run: MathSegment[] = [];

  const flushRun = () => {
    if (!run.length) return;
    const t = run[0].type;
    const merged: MathSegment = {
      type: t === 'display' ? 'display' : 'inline',
      content: run
        .map((s) => s.content.trim())
        .filter(Boolean)
        .join(' \\, '),
    };
    out.push(merged);
    run = [];
  };

  for (const seg of segments) {
    if (seg.type === 'text') {
      flushRun();
      out.push(seg);
      continue;
    }
    if (run.length && run[0].type !== seg.type) {
      flushRun();
    }
    run.push(seg);
  }
  flushRun();
  return out;
}

/**
 * Cùng logic edu-app-backend/scripts/katex-mixed-html.mjs: delimiter + legacy auto-math + gộp inline liền kề.
 */
export function getMixedMathSegments(input: string): MathSegment[] {
  const raw = normalizeMathInput(input ?? '');
  let segments = splitMixedMath(raw);

  if (segments.length === 1 && segments[0].type === 'text') {
    const legacy = splitLegacyMathText(raw);
    if (legacy.some((segment) => segment.type !== 'text')) {
      segments = legacy;
    }
  }

  return mergeAdjacentMathSegments(segments);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function katexToHtml(latex: string, displayMode: boolean): string {
  const trimmed = normalizeMathInput(latex).trim();
  if (!trimmed) return '';
  try {
    return katex.renderToString(trimmed, {
      displayMode,
      throwOnError: false,
      strict: 'ignore',
      trust: false,
    });
  } catch {
    return escapeHtml(trimmed);
  }
}

export function mathToPlainText(text: string): string {
  const segments = getMixedMathSegments(text ?? '');
  return segments
    .map((seg) => {
      if (seg.type === 'text') return seg.content;

      return seg.content
        .replace(/\\dfrac/g, '\\frac')
        .replace(/\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, '($1)/($2)')
        .replace(/\\sqrt\s*\{([^{}]+)\}/g, '√($1)')
        .replace(/\\times/g, '×')
        .replace(/\\cdot/g, '·')
        .replace(/\\leq/g, '≤')
        .replace(/\\geq/g, '≥')
        .replace(/\\neq/g, '≠')
        .replace(/\\approx/g, '≈')
        .replace(/\\infty/g, '∞')
        .replace(/\\pi/g, 'π')
        .replace(/\\int/g, '∫')
        .replace(/\\sum/g, 'Σ')
        .replace(/\\lim/g, 'lim')
        .replace(/\\left/g, '')
        .replace(/\\right/g, '')
        .replace(/\\,/g, ' ')
        .replace(/\\;/g, ' ')
        .replace(/\\:/g, ' ')
        .replace(/\\!/g, '')
        .replace(/\\begin\{[a-zA-Z*]+\}/g, '')
        .replace(/\\end\{[a-zA-Z*]+\}/g, '')
        .replace(/\\\\/g, ' | ')
        .replace(/\{([^{}]+)\}/g, '$1')
        .replace(/\^/g, '^')
        .replace(/_/g, '_')
        .trim();
    })
    .join('');
}
